import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { Chip, Stack, keyframes } from '@mui/material';
import React from 'react';
import {
    formatIdleFlagText,
    resolveIdleLabel,
} from '../../helpers/idlePrinters';
import { productivityChipLabel } from '../../helpers/printerProductivity';
import { useTickingNow } from '../../hooks/useTickingNow';
import Printer3dIcon from '../icons/Printer3dIcon';

const idlePulse = keyframes`
    0%, 100% {
        box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.65);
        filter: brightness(1);
    }
    50% {
        box-shadow: 0 0 0 8px rgba(211, 47, 47, 0);
        filter: brightness(1.12);
    }
`;

/** Flag chamativa: impressora ociosa há mais de 10 minutos (tempo sobe no front). */
export function PrinterIdleFlag({
    idleLabel,
    lastActivityAt = null,
    size = 'small',
    sx,
}) {
    const now = useTickingNow(15000);
    const liveLabel = lastActivityAt != null
        ? resolveIdleLabel(lastActivityAt, now)
        : (idleLabel || 'sem jobs');
    const text = formatIdleFlagText(liveLabel);
    if (!text) {
        return null;
    }

    return (
        <Chip
            size={size}
            icon={<HourglassBottomIcon sx={{ fontSize: '1rem !important' }} />}
            label={text}
            color="error"
            variant="filled"
            sx={{
                fontWeight: 800,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                animation: `${idlePulse} 1.5s ease-in-out infinite`,
                ...sx,
            }}
        />
    );
}

/** Status runtime: imprimindo, não produtiva, ociosa (>10 min) ou parada recente. */
export default function PrinterRuntimeStatus({
    isPrinting = false,
    isIdle = false,
    idleLabel = null,
    lastActivityAt = null,
    restriction = null,
    size = 'small',
    showRecentStopped = true,
}) {
    const restrictionChip = restriction ? (
        <Chip
            size={size}
            label={productivityChipLabel(restriction)}
            color="warning"
            variant="filled"
        />
    ) : null;

    if (isPrinting) {
        return (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                <Chip
                    size={size}
                    icon={<Printer3dIcon fontSize="small" />}
                    label="Imprimindo"
                    color="success"
                    variant="filled"
                />
                {restrictionChip}
            </Stack>
        );
    }
    if (restrictionChip) {
        return restrictionChip;
    }
    if (isIdle) {
        return (
            <PrinterIdleFlag
                idleLabel={idleLabel || 'sem jobs'}
                lastActivityAt={lastActivityAt}
                size={size}
            />
        );
    }
    if (!showRecentStopped) {
        return null;
    }
    return (
        <Chip
            size={size}
            label="Parada"
            variant="outlined"
            color="default"
        />
    );
}

export function idleCardSx(isPrinting, isIdle, restriction) {
    if (isPrinting) {
        return {
            borderColor: 'success.main',
            boxShadow: '0 0 0 1px rgba(46, 125, 50, 0.35)',
        };
    }
    if (restriction) {
        return {
            borderColor: 'warning.main',
            boxShadow: '0 0 0 1px rgba(237, 108, 2, 0.35)',
        };
    }
    if (isIdle) {
        return {
            borderColor: 'error.main',
            boxShadow: '0 0 0 2px rgba(211, 47, 47, 0.35)',
        };
    }
    return {
        borderColor: 'divider',
        boxShadow: 'none',
    };
}
